import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import { fetchStudentsForPrincipal } from '../../services/principalStudents';
import { fetchDeliveredNotesForStudent } from '../../services/SongNotesService';
import { groupDeliveredBySong, SONG_NOTE_STATUS } from '../../services/songNotesModel';
import { fetchTracksForStudent } from '../../services/StudentTracksService';
import { mergeTracksIntoGroups, groupFilesByLength, trackLabel, isSheet, playbackUrlFor, trackKey, driveLinkOf } from '../../services/studentTracksModel';
import useTrackPlayer from '../../components/Tracks/useTrackPlayer';
import {
	fetchQuestionsForStudent,
	createSongQuestion,
} from '../../services/SongQuestionsService';
import {
	partitionQuestions,
	questionsForSong,
	noteForQuestion,
} from '../../services/songQuestionsModel';
import AskAboutSong from '../../components/CustomerPortal/AskAboutSong';
import { PageWrap, PageHead, Card, QuietState, studentDisplayName } from './customerUi';
import KeyControl from '../../components/Tracks/KeyControl';

/* Songs — the family-facing READ surface for Song Notes (arc item 5, 2026-08-03).
   Replaces the reserved empty state that shipped with the customer shell.

   WHAT A FAMILY MAY SEE IS DECIDED BY `status`, NOT BY THIS FILE. A `captured`
   note is one the teacher deliberately withheld — including feedback held back
   so as not to overwhelm a student — and core.md is explicit that CYV never
   shows a family feedback the teacher chose not to give. That exclusion is
   asserted in three independent places: the Firestore rule, the query in
   SongNotesService, and familyVisibleNotes() inside groupDeliveredBySong. This
   component adds no fourth filter of its own, because a UI filter is not a wall
   and pretending otherwise is the pattern core.md forbids.

   ONE QUERY PER STUDENT, not one per family. Family membership lives on
   students.familyId and is resolved by the rule through a get() — never
   denormalized onto a note, since a copied familyId goes stale the day a family
   merges. See SongNotesService.fetchDeliveredNotesForStudent.

   READ-ONLY, permanently. Song Notes are teacher-write-only; there is no write
   path from a family into teacher-authored feedback, and none should be added
   here. */

const StatusChip = ({ status }) => {
	const landed = status === SONG_NOTE_STATUS.LANDED;
	return (
		<span
			style={{
				flex: '0 0 auto',
				fontSize: '0.62rem',
				fontWeight: 'var(--fw-bold, 700)',
				letterSpacing: '0.09em',
				textTransform: 'uppercase',
				borderRadius: 999,
				padding: '3px 9px',
				lineHeight: 1.6,
				color: landed
					? 'var(--cyv-teal-600, #2f7d77)'
					: 'var(--cyv-warning, #b57a1e)',
				background: landed
					? 'color-mix(in srgb, var(--cyv-teal-600, #54ada7) 14%, var(--surface-card, #fff))'
					: 'color-mix(in srgb, var(--cyv-warning, #d99633) 15%, var(--surface-card, #fff))',
			}}>
			{landed ? 'Landed' : 'Working on'}
		</span>
	);
};

/* "Ask" sits on the note row itself, not only on the song. "A question about one
   of our notes" and "a question about the song" are different asks, and the
   first is the more useful of the two in a lesson — it tells the teacher exactly
   where to start. Making it a per-note affordance is what captures that anchor
   without asking the parent to describe which note they mean. */
const AskLink = ({ label = 'Ask', onClick }) => (
	<button
		type='button'
		onClick={onClick}
		style={{
			flex: '0 0 auto',
			appearance: 'none',
			border: 'none',
			background: 'transparent',
			padding: '4px 6px',
			margin: '-4px -6px -4px 0',
			fontFamily: 'inherit',
			fontSize: '0.78rem',
			fontWeight: 'var(--fw-semibold, 600)',
			color: 'var(--cyv-teal-600, #2f7d77)',
			cursor: 'pointer',
			display: 'inline-flex',
			alignItems: 'center',
			gap: 5,
		}}>
		<i className='fa-sharp fa-solid fa-circle-question' aria-hidden='true' />
		{label}
	</button>
);

const QuestionRow = ({ question, note }) => {
	const answered = question.status === 'resolved';
	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'flex-start',
				gap: 10,
				padding: '10px 15px',
				borderTop: '1px solid var(--cyv-line, #f0eae1)',
			}}>
			<i
				className={`fa-sharp fa-solid ${answered ? 'fa-circle-check' : 'fa-circle-question'}`}
				aria-hidden='true'
				style={{
					marginTop: 3,
					fontSize: '0.8rem',
					color: answered
						? 'var(--cyv-teal-600, #2f7d77)'
						: 'var(--text-muted, #a6adba)',
				}}
			/>
			<div style={{ flex: 1, minWidth: 0 }}>
				{/* The note it refers to, if any — resolved by an in-memory join, so a
				    question whose note was deleted still renders (with no quote)
				    rather than disappearing. */}
				{note && (
					<div
						style={{
							fontSize: '0.76rem',
							lineHeight: 1.45,
							color: 'var(--text-muted, #7b8499)',
							marginBottom: 3,
						}}>
						re: “{note.text}”
					</div>
				)}
				<div
					style={{
						fontSize: '0.88rem',
						lineHeight: 1.55,
						color: 'var(--text-body, #4a5468)',
					}}>
					{question.text}
				</div>
			</div>
			<span
				style={{
					flex: '0 0 auto',
					fontSize: '0.62rem',
					fontWeight: 'var(--fw-bold, 700)',
					letterSpacing: '0.09em',
					textTransform: 'uppercase',
					color: answered ? 'var(--cyv-teal-600, #2f7d77)' : 'var(--text-muted, #7b8499)',
				}}>
				{answered ? 'Answered' : 'Asked'}
			</span>
		</div>
	);
};

const NoteRow = ({ note, last, onAsk }) => (
	<div
		style={{
			display: 'flex',
			alignItems: 'flex-start',
			gap: 10,
			padding: '11px 15px',
			borderBottom: last ? 'none' : '1px solid var(--cyv-line, #f0eae1)',
		}}>
		<span
			style={{
				flex: 1,
				minWidth: 0,
				fontSize: '0.9rem',
				lineHeight: 1.55,
				color: 'var(--text-body, #4a5468)',
			}}>
			{note.text}
		</span>
		<AskLink onClick={() => onAsk(note)} />
		<StatusChip status={note.status} />
	</div>
);

/* Tracks — the family's reference and practice files for this song (music
   library arc, 2026-09-14; inline playback 2026-09-16). A file the filing
   routine has copied into Firebase Storage PLAYS HERE, in the page's one
   player, from our own project — never through Drive's page, which stalls at
   0:00 for a parent whose Google session has gone stale (found live 09-16).
   A file with no copy yet stays a link to Drive, and the Sheet PDF is always
   a link: the destination is another site, a new tab is the honest behaviour,
   and a long-press on a phone should offer "copy link". One row per Length
   ("Cut 60s", "Full") so a song that has both never reads as one ambiguous
   pile. Always visible, never hover-gated; the pairing copies StatusChip's
   proven teal-on-tint, so it reads in every theme. */
const pillStyle = (active) => ({
	display: 'inline-flex',
	alignItems: 'center',
	gap: 7,
	textDecoration: 'none',
	font: 'inherit',
	fontSize: '0.82rem',
	fontWeight: 'var(--fw-semibold, 600)',
	lineHeight: 1.2,
	padding: '7px 12px',
	border: 0,
	borderRadius: 999,
	cursor: 'pointer',
	color: active ? 'var(--cyv-teal-800, #2f7d77)' : 'var(--cyv-teal-600, #2f7d77)',
	background: active
		? 'color-mix(in srgb, var(--cyv-teal-600, #54ada7) 28%, var(--surface-card, #fff))'
		: 'color-mix(in srgb, var(--cyv-teal-600, #54ada7) 14%, var(--surface-card, #fff))',
});

const TrackLinks = ({ files, player }) => {
	const rows = groupFilesByLength(files);
	if (rows.length === 0) return null;
	const current = player && player.current;
	const currentHere = Boolean(current && current.engine === 'audio' && (files || []).some((f) => trackKey(f) === current.key));
	return (
		<div style={{ padding: '2px 15px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
			{rows.map((row) => (
				<div key={row.length} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
					{rows.length > 1 && (
						<span
							style={{
								flex: '0 0 auto',
								fontSize: '0.68rem',
								fontWeight: 'var(--fw-bold, 700)',
								letterSpacing: '0.09em',
								textTransform: 'uppercase',
								color: 'var(--text-muted, #7b8499)',
								marginRight: 2,
							}}>
							{row.length}
						</span>
					)}
					{row.files.map((file) => {
						const playable = player && !isSheet(file) && playbackUrlFor(file);
						if (playable) {
							const active = player.isCurrent(file);
							const playing = active && player.isPlaying;
							return (
								<button
									key={trackKey(file)}
									type='button'
									aria-pressed={active}
									title={file.name || trackLabel(file)}
									style={pillStyle(active)}
									onClick={() => player.toggle(file)}>
									<i
										className={`fa-sharp fa-solid ${playing ? 'fa-pause' : 'fa-play'}`}
										aria-hidden='true'
										style={{ fontSize: '0.72rem' }}
									/>
									{trackLabel(file)}
								</button>
							);
						}
						return (
							<a
								key={trackKey(file)}
								href={driveLinkOf(file) || playbackUrlFor(file)}
								target='_blank'
								rel='noopener noreferrer'
								title={file.name || trackLabel(file)}
								style={pillStyle(false)}>
								<i
									className={`fa-sharp fa-solid ${isSheet(file) ? 'fa-file-pdf' : 'fa-play'}`}
									aria-hidden='true'
									style={{ fontSize: '0.72rem' }}
								/>
								{trackLabel(file)}
							</a>
						);
					})}
				</div>
			))}
			{currentHere && (
				<div
					data-testid='family-track-transport'
					style={{
						display: 'flex',
						flexWrap: 'wrap', // the key control drops to its own line on a phone
						alignItems: 'center',
						gap: 10,
						minWidth: 0,
						padding: '6px 10px',
						borderRadius: 12,
						background: 'color-mix(in srgb, var(--cyv-teal-600, #54ada7) 14%, var(--surface-card, #fff))',
						color: 'var(--cyv-teal-800, #2f7d77)',
					}}>
					<button
						type='button'
						aria-label={player.isPlaying ? 'Pause' : 'Play'}
						onClick={() => player.toggle(current.file)}
						style={{
							display: 'inline-flex',
							alignItems: 'center',
							justifyContent: 'center',
							flex: '0 0 auto',
							width: 32,
							height: 32,
							padding: 0,
							border: 0,
							borderRadius: '50%',
							background: 'var(--cyv-teal-800, #2f7d77)',
							color: 'var(--surface-card, #fff)',
							fontSize: 12,
							cursor: 'pointer',
						}}>
						<i className={`fa-sharp fa-solid ${player.isPlaying ? 'fa-pause' : 'fa-play'}`} aria-hidden='true' />
					</button>
					<span style={{ flex: '0 0 auto', fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
						{player.formatTime(player.currentTime)}
						<span style={{ color: 'var(--text-muted, #7b8499)' }}> / </span>
						{player.formatTime(player.duration)}
					</span>
					<input
						type='range'
						min='0'
						max={player.duration > 0 ? player.duration : 0}
						step='0.1'
						value={player.duration > 0 ? player.currentTime : 0}
						aria-label='Seek'
						onChange={(e) => player.seek(parseFloat(e.target.value))}
						style={{ flex: '1 1 60px', minWidth: 60, height: 18, margin: 0, accentColor: 'var(--cyv-teal-800, #2f7d77)', cursor: 'pointer' }}
					/>
					<a
						href={playbackUrlFor(current.file)}
						target='_blank'
						rel='noopener noreferrer'
						title='Download'
						style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flex: '0 0 auto', fontSize: 12, fontWeight: 600, color: 'var(--cyv-teal-800, #2f7d77)', textDecoration: 'none', whiteSpace: 'nowrap' }}>
						<i className='fa-sharp fa-solid fa-arrow-down-to-line' aria-hidden='true' />
						Download
					</a>
					<KeyControl player={player} />
				</div>
			)}
			{currentHere && player.error && (
				<p role='alert' style={{ margin: 0, fontSize: 12, color: 'var(--cyv-danger, #b3261e)' }}>
					{player.error.replace('open it in Drive', 'try the Drive link')}{' '}
					{driveLinkOf(current.file) && (
						<a href={driveLinkOf(current.file)} target='_blank' rel='noopener noreferrer'>
							Open in Drive
						</a>
					)}
				</p>
			)}
		</div>
	);
};

const SongCard = ({ group, questions, notes, composer, onOpenComposer, onCloseComposer, onSubmit, player = null }) => {
	// Landed after focus: what the student is still working on is the live
	// information; what already landed is the reward for scrolling.
	const ordered = [...group.focus, ...group.landed];
	const { open, resolved } = partitionQuestions(questions);
	// Open first, and open sorts oldest-first inside partitionQuestions — an
	// unanswered question that has survived two lessons should not be buried
	// under last night's.
	const shownQuestions = [...open, ...resolved];

	return (
		<Card style={{ marginBottom: 14 }}>
			<div
				style={{
					display: 'flex',
					alignItems: 'baseline',
					gap: 8,
					padding: '13px 15px 9px',
				}}>
				<span
					style={{
						fontFamily: 'var(--font-serif)',
						fontSize: '1.02rem',
						fontWeight: 'var(--fw-semibold, 600)',
						color: 'var(--text-heading, #384971)',
						minWidth: 0,
					}}>
					{group.songName}
				</span>
				<span
					style={{
						marginLeft: 'auto',
						flex: '0 0 auto',
						fontSize: '0.72rem',
						color: 'var(--text-muted, #a6adba)',
					}}>
					{group.total > 0
						? `${group.total} ${group.total === 1 ? 'note' : 'notes'}`
						: `${(group.tracks || []).length} ${(group.tracks || []).length === 1 ? 'track' : 'tracks'}`}
				</span>
			</div>
			<TrackLinks files={group.tracks} player={player} />
			{ordered.map((note, i) => (
				<NoteRow
					key={note.id}
					note={note}
					last={i === ordered.length - 1 && shownQuestions.length === 0}
					onAsk={(n) => onOpenComposer({ songId: group.songId, songName: group.songName, noteId: n.id })}
				/>
			))}

			{shownQuestions.map((question) => (
				<QuestionRow
					key={question.id}
					question={question}
					note={noteForQuestion(question, notes)}
				/>
			))}

			{composer ? (
				<AskAboutSong
					note={composer.noteId ? notes.find((n) => n.id === composer.noteId) || null : null}
					busy={composer.busy}
					error={composer.error}
					onCancel={onCloseComposer}
					onSubmit={(text) => onSubmit(text)}
				/>
			) : (
				<div
					style={{
						display: 'flex',
						padding: '9px 15px 11px',
						borderTop: '1px solid var(--cyv-line, #f0eae1)',
					}}>
					<AskLink
						label='Ask about this song'
						onClick={() => onOpenComposer({ songId: group.songId, songName: group.songName, noteId: null })}
					/>
				</div>
			)}
		</Card>
	);
};

/* The calm empty state the reserved v1 shipped, kept almost verbatim — minus its
   "Coming this school year" badge, which stopped being true the moment this
   surface went live. Notes genuinely will not appear until a teacher delivers
   one, so this has to read as "nothing yet", never as "something is broken". */
const NothingYet = () => (
	<div
		style={{
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			textAlign: 'center',
			gap: 13,
			padding: '2.5rem 1.25rem 3.5rem',
		}}>
		<span
			style={{
				width: 64,
				height: 64,
				borderRadius: '50%',
				background:
					'color-mix(in srgb, var(--cyv-teal-600, #54ada7) 14%, var(--surface-card, #fff))',
				color: 'var(--cyv-teal-600, #2f7d77)',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}>
			<i className='fa-sharp fa-solid fa-music' style={{ fontSize: 24 }} aria-hidden='true' />
		</span>
		<div
			style={{
				fontFamily: 'var(--font-serif)',
				fontSize: '1.25rem',
				fontWeight: 'var(--fw-semibold, 600)',
				color: 'var(--text-heading, #384971)',
				maxWidth: 260,
				lineHeight: 1.3,
			}}>
			Your teacher’s notes will appear here
		</div>
		<div
			style={{
				fontSize: '0.9rem',
				color: 'var(--text-muted, #7b8499)',
				maxWidth: 300,
				lineHeight: 1.6,
			}}>
			Songs and focus areas from each lesson land in this tab once your teacher
			shares them.
		</div>
	</div>
);

const Songs = () => {
	const { userProfile, currentUser } = useAuth();
	const familyId = userProfile?.familyId || null;
	// P2b: a student principal holds BOTH scalars, so familyId alone no longer
	// identifies the read. Depend on the primitives, not on userProfile's
	// object identity, and let the resolver issue the per-principal query.
	const studentId = userProfile?.studentId || null;
	const role = userProfile?.role || null;

	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);

	/* ONE composer open at a time, page-wide. Two half-written questions on one
	   page is a way to lose one, and the anchor (which song, which note) is only
	   unambiguous while exactly one field is open. Keyed by studentId+songId so a
	   two-child family can't open the same song twice. */
	const [composer, setComposer] = useState(null);
	// ONE track player for the whole page — every song card's pills play
	// through it, so two cuts never play at once (same rule as the lesson).
	const trackPlayer = useTrackPlayer();

	useEffect(() => {
		if (!familyId) {
			setLoading(false);
			return undefined;
		}
		let cancelled = false;
		(async () => {
			try {
				const kids = await fetchStudentsForPrincipal({ role, familyId, studentId });
				const withNotes = await Promise.all(
					kids.map(async (student) => {
						/* Notes and questions load together per student. The questions
						   read is the SAME family-resolution path as the notes read (a
						   per-doc get() against students/{id}), and the query constrains
						   studentId itself because rules are not filters.

						   THE QUESTIONS READ CANNOT BE ALLOWED TO FAIL THE PAGE. Notes
						   are the primary content and questions are additive, so they
						   get their own catch: a shared try would let a secondary,
						   newer feature blank a surface families already depend on.
						   That is not hypothetical — a composite index that has not
						   finished building returns FAILED_PRECONDITION on this exact
						   query, which is a live window on every deploy that adds one.
						   Degrading to "no questions" loses nothing a family cannot see
						   by reloading; blanking the page loses their teacher's notes. */
						const [notes, questions, tracks] = await Promise.all([
							fetchDeliveredNotesForStudent(student.id),
							fetchQuestionsForStudent(student.id).catch((e) => {
								console.error('Song questions unavailable for', student.id, e);
								return [];
							}),
							/* Tracks are additive in exactly the way questions are, and get
							   the same treatment: their read must never blank the notes. */
							fetchTracksForStudent(student.id).catch((e) => {
								console.error('Student tracks unavailable for', student.id, e);
								return [];
							}),
						]);
						return {
							student,
							notes,
							questions,
							groups: mergeTracksIntoGroups(groupDeliveredBySong(notes), tracks),
						};
					})
				);
				if (!cancelled) setRows(withNotes);
			} catch (err) {
				console.error('Error loading song notes:', err);
				if (!cancelled) setLoadError(true);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [role, familyId, studentId]);

	const openComposer = (studentId, { songId, songName, noteId }) =>
		setComposer({ studentId, songId, songName, noteId, busy: false, error: null });

	const submitQuestion = async (student, text) => {
		if (!composer || composer.busy) return;
		setComposer((c) => ({ ...c, busy: true, error: null }));
		try {
			const created = await createSongQuestion({
				studentId: student.id,
				songId: composer.songId,
				songName: composer.songName,
				songNoteId: composer.noteId,
				text,
				askedByUid: currentUser?.uid,
				askedByName:
					userProfile?.firstName ||
					userProfile?.displayName ||
					currentUser?.displayName ||
					'',
			});
			// Reflect it locally rather than refetching: the write is small, the
			// echoed row already carries a client timestamp for sorting, and a
			// refetch here would re-read every student on the page.
			setRows((prev) =>
				prev.map((r) =>
					r.student.id === student.id ? { ...r, questions: [created, ...r.questions] } : r
				)
			);
			setComposer(null);
		} catch (err) {
			console.error('Failed to send song question:', err);
			setComposer((c) =>
				c ? { ...c, busy: false, error: "We couldn't send that just now. Please try again." } : c
			);
		}
	};

	const hasAnyNotes = rows.some((r) => r.groups.length > 0);
	// The per-student heading earns its space only when there is more than one
	// child; a single-student family should not read as a list of one.
	const showStudentHeadings = rows.length > 1;

	return (
		<PageWrap>
			<PageHead
				title='Songs'
				subtitle='What you’re working on, and what has landed.'
			/>

			{loading && (
				<p style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--text-muted)' }}>
					Loading…
				</p>
			)}

			{!loading && loadError && (
				<Card style={{ padding: '15px' }}>
					<p style={{ margin: 0, color: 'var(--text-body)' }}>
						We couldn’t load your notes just now. Please refresh, or{' '}
						<a href='/contact'>contact us</a> if it keeps happening.
					</p>
				</Card>
			)}

			{!loading && !loadError && !familyId && (
				<Card style={{ padding: '15px' }}>
					<p style={{ margin: 0, color: 'var(--text-body)' }}>
						Your account isn’t linked to a family yet. If you think this is a
						mistake, <a href='/contact'>contact us</a> and we’ll get you set up.
					</p>
				</Card>
			)}

			{!loading && !loadError && familyId && !hasAnyNotes && <NothingYet />}

			{!loading &&
				!loadError &&
				hasAnyNotes &&
				rows.map(({ student, groups, questions, notes }) => (
					<section key={student.id} style={{ marginBottom: showStudentHeadings ? 22 : 0 }}>
						{showStudentHeadings && (
							<div
								style={{
									fontSize: '0.68rem',
									fontWeight: 'var(--fw-bold, 700)',
									letterSpacing: '0.12em',
									textTransform: 'uppercase',
									color: 'var(--text-muted, #a6adba)',
									padding: '0 2px 8px',
								}}>
								{studentDisplayName(student)}
							</div>
						)}
						{groups.length === 0 ? (
							<Card style={{ marginBottom: 14 }}>
								<QuietState icon='fa-music'>
									Nothing shared yet for {studentDisplayName(student)}.
								</QuietState>
							</Card>
						) : (
							groups.map((group) => (
								<SongCard
									key={group.songId || group.songName}
									group={group}
									player={trackPlayer}
									notes={notes}
									questions={questionsForSong(questions, group)}
									composer={
										composer &&
										composer.studentId === student.id &&
										composer.songId === group.songId
											? composer
											: null
									}
									onOpenComposer={(anchor) => openComposer(student.id, anchor)}
									onCloseComposer={() => setComposer(null)}
									onSubmit={(text) => submitQuestion(student, text)}
								/>
							))
						)}
					</section>
				))}
		</PageWrap>
	);
};

export default Songs;
